import React, { useEffect, useRef, useState } from 'react';
import { trackWaitlistFormError, trackWaitlistFormStart, trackWaitlistFormSuccess } from '../utils/analytics';
import { dismissPopupForSession, markPopupSubscribed } from './AutoPopup';
import './JoinRitualsModal.css';

export default function JoinRitualsModal({ isOpen, onClose }) {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState({ type: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modalData, setModalData] = useState(null);
    const emailRef = useRef(null);
    const closeRef = useRef(null);
    const submittingRef = useRef(false);

    useEffect(() => {
        fetch('/api/content/join-modal/')
            .then(response => response.ok ? response.json() : null)
            .then(setModalData)
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        dismissPopupForSession();
        const previousFocus = document.activeElement;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        setStatus({ type: '', message: '' });
        const focusTimer = window.setTimeout(() => emailRef.current?.focus(), 0);
        const onKeyDown = event => {
            if (event.key === 'Escape') onClose();
            if (event.key === 'Tab') {
                const first = closeRef.current;
                const last = document.querySelector('.melanua-popup__submit');
                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last?.focus();
                } else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first?.focus();
                }
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            window.clearTimeout(focusTimer);
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = previousOverflow;
            previousFocus?.focus?.();
        };
    }, [isOpen, onClose]);

    const title = modalData?.title || 'Stay close to MELANUA.';
    const subtitle = modalData?.subtitle || 'Receive considered notes on new releases, formulation, the MELANUA Journal and moments from the House.';
    const emailLabel = modalData?.email_label || 'Email address';
    const emailPlaceholder = modalData?.email_placeholder || 'Your email address';
    const submitButtonText = modalData?.submit_button_text || 'JOIN THE HOUSE';
    const submittingText = modalData?.submitting_text || 'JOINING…';
    const successMessage = modalData?.success_message || 'Thank you for joining the House.';
    const privacyNote = modalData?.privacy_note || 'By subscribing, you agree to receive occasional communications from MELANUA®. You may unsubscribe at any time.';

    const handleSubmit = async event => {
        event.preventDefault();
        if (submittingRef.current || status.type === 'success') return;
        submittingRef.current = true;
        setIsSubmitting(true);
        setStatus({ type: '', message: '' });
        trackWaitlistFormStart();
        try {
            const response = await fetch('/api/waitlist/subscribe/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': document.querySelector('[name=csrfmiddlewaretoken]')?.value || ''
                },
                body: JSON.stringify({
                    email: email.trim(),
                    source: 'join_rituals_modal',
                    marketing_consent: true
                })
            });
            const data = await response.json();
            if (!response.ok) {
                const message = data.errors?.email?.[0] || data.message || 'Something went wrong. Please try again.';
                setStatus({ type: 'error', message });
                trackWaitlistFormError(message);
                return;
            }
            setStatus({ type: 'success', message: successMessage });
            setEmail('');
            trackWaitlistFormSuccess();
            markPopupSubscribed();
        } catch (_) {
            const message = 'Please check your connection and try again.';
            setStatus({ type: 'error', message });
            trackWaitlistFormError('Network error');
        } finally {
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="melanua-popup__overlay" onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }}>
            <section className="melanua-popup" role="dialog" aria-modal="true" aria-labelledby="melanua-popup-title" aria-describedby="melanua-popup-description">
                <button ref={closeRef} className="melanua-popup__close" type="button" aria-label="Close newsletter signup" onClick={onClose}>
                    <span aria-hidden="true">×</span>
                </button>
                <img className="melanua-popup__emblem" src={modalData?.logo_image || '/static/M_Emblem.webp'} alt="" width="40" height="40" />
                <h2 id="melanua-popup-title">{title}</h2>
                <p id="melanua-popup-description" className="melanua-popup__description">{subtitle}</p>
                <form className="melanua-popup__form" onSubmit={handleSubmit}>
                    <label htmlFor="melanua-popup-email">{emailLabel}</label>
                    <input ref={emailRef} id="melanua-popup-email" type="email" name="email" autoComplete="email" required
                        value={email} onChange={event => setEmail(event.target.value)} placeholder={emailPlaceholder} />
                    <button className="melanua-popup__submit" type="submit" disabled={isSubmitting || status.type === 'success'}>
                        {isSubmitting ? submittingText : submitButtonText}
                    </button>
                    {status.message && <p className={`melanua-popup__status melanua-popup__status--${status.type}`} role={status.type === 'error' ? 'alert' : 'status'}>{status.message}</p>}
                </form>
                <p className="melanua-popup__legal">{privacyNote}</p>
            </section>
        </div>
    );
}
